/** End-of-day close helpers — mirrors mobile shop_floor EOD models/UI rules. */

export const EOD_CLOSE_PERMISSION = 'manufacturing.wo.eod_close';
export const EOD_COMPLETE_PERMISSION = 'manufacturing.wo.complete';

export type EodCloseOutcome = 'COMPLETE' | 'PAUSE' | 'IN_PROGRESS';

export type EodEligibleStage = {
  id: string;
  work_order_id: string;
  work_order_number: string | null;
  work_order_status: string | null;
  stage_code: string;
  stage_name: string;
  status: string;
  inspection_required: boolean;
  inspection_status: string | null;
  assigned_to_user_id: string | null;
  workstation_id: string | null;
  inspector_user_id: string | null;
  verifier_user_id: string | null;
  production_unit_number: number | null;
  work_order_line_item_id: string | null;
  line_number: number | null;
  item_code: string | null;
  item_description: string | null;
  uom: string | null;
  planned_quantity: number | null;
  estimated_hours: number | null;
  started_date: string | null;
  actual_hours: number | null;
  notes: string | null;
  workstation_code: string | null;
  workstation_name: string | null;
  operator_name: string | null;
  inspector_name: string | null;
  verifier_name: string | null;
  /** Running qty done (sum of saved register rows). */
  completed_qty: number;
  /** Latest saved register row for this stage ("copy yesterday"). */
  last_progress: EodLastProgress | null;
};

export type EodLastProgress = {
  work_date: string;
  outcome: string;
  started_at: string | null;
  ended_at: string | null;
  hours: number;
  qty_done: number | null;
  operator_user_id: string | null;
  workstation_id: string | null;
  notes: string | null;
};

export type EodCloseStageInput = {
  stageId: string;
  outcome: EodCloseOutcome;
  actualHours: number;
  notes: string;
  completedQty?: number | null;
  qtyDone?: number | null;
  workDate?: string | null;
  clientRowKey?: string | null;
  autoPassInspection?: boolean;
  startedAt?: string | null;
  endedAt?: string | null;
  workstationId?: string | null;
  assignedToUserId?: string | null;
  inspectorUserId?: string | null;
  verifierUserId?: string | null;
};

export type EodCloseStageResult = {
  stageId: string;
  ok: boolean;
  error?: string;
  warning?: string;
  duplicate?: boolean;
  outcome?: EodCloseOutcome | string;
  workOrderId?: string;
  stageCode?: string;
  stageName?: string;
};

const ISO_DT_RE =
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;

function pickStr(raw: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = raw[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s.toLowerCase() !== 'null') return String(v);
  }
  return null;
}

function pickNum(raw: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = raw[k];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function normalizeEodEligibleStage(raw: Record<string, unknown>): EodEligibleStage {
  return {
    id: String(raw.id ?? ''),
    work_order_id: pickStr(raw, ['work_order_id', 'workOrderId']) ?? '',
    work_order_number: pickStr(raw, ['work_order_number', 'workOrderNumber']),
    work_order_status: pickStr(raw, ['work_order_status', 'workOrderStatus']),
    stage_code: pickStr(raw, ['stage_code', 'stageCode']) ?? '',
    stage_name: pickStr(raw, ['stage_name', 'stageName']) ?? '',
    status: String(raw.status ?? ''),
    inspection_required:
      raw.inspection_required === true ||
      raw.inspection_required === 1 ||
      raw.inspectionRequired === true,
    inspection_status: pickStr(raw, ['inspection_status', 'inspectionStatus']),
    assigned_to_user_id: pickStr(raw, ['assigned_to_user_id', 'assignedToUserId']),
    workstation_id: pickStr(raw, ['workstation_id', 'workstationId']),
    inspector_user_id: pickStr(raw, ['inspector_user_id', 'inspectorUserId']),
    verifier_user_id: pickStr(raw, ['verifier_user_id', 'verifierUserId']),
    production_unit_number: pickNum(raw, ['production_unit_number', 'productionUnitNumber']),
    work_order_line_item_id: pickStr(raw, ['work_order_line_item_id', 'workOrderLineItemId']),
    line_number: pickNum(raw, ['line_number', 'lineNumber']),
    item_code: pickStr(raw, ['item_code', 'itemCode']),
    item_description: pickStr(raw, [
      'item_description',
      'itemDescription',
      'line_item_description',
      'lineItemDescription',
      'work_order_item_name',
      'workOrderItemName',
      'item_name',
      'itemName',
    ]),
    uom: pickStr(raw, ['uom']),
    planned_quantity: pickNum(raw, ['planned_quantity', 'plannedQuantity']),
    estimated_hours: pickNum(raw, ['estimated_hours', 'estimatedHours']),
    started_date: pickStr(raw, ['started_date', 'startedDate']),
    actual_hours: pickNum(raw, ['actual_hours', 'actualHours']),
    notes: pickStr(raw, ['notes']),
    workstation_code: pickStr(raw, ['workstation_code', 'workstationCode']),
    workstation_name: pickStr(raw, ['workstation_name', 'workstationName']),
    operator_name: pickStr(raw, ['operator_name', 'operatorName']),
    inspector_name: pickStr(raw, ['inspector_name', 'inspectorName']),
    verifier_name: pickStr(raw, ['verifier_name', 'verifierName']),
    completed_qty: pickNum(raw, ['completed_qty', 'completedQty']) ?? 0,
    last_progress: normalizeLastProgress(raw.last_progress ?? raw.lastProgress),
  };
}

function normalizeLastProgress(raw: unknown): EodLastProgress | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    work_date: pickStr(r, ['work_date', 'workDate']) ?? '',
    outcome: pickStr(r, ['outcome']) ?? '',
    started_at: pickStr(r, ['started_at', 'startedAt']),
    ended_at: pickStr(r, ['ended_at', 'endedAt']),
    hours: pickNum(r, ['hours']) ?? 0,
    qty_done: pickNum(r, ['qty_done', 'qtyDone']),
    operator_user_id: pickStr(r, ['operator_user_id', 'operatorUserId']),
    workstation_id: pickStr(r, ['workstation_id', 'workstationId']),
    notes: pickStr(r, ['notes']),
  };
}

export function requiresCompletedQty(stage: Pick<EodEligibleStage, 'planned_quantity'>): boolean {
  return (stage.planned_quantity ?? 0) > 0;
}

export function hasMissingAssignments(
  stage: Pick<
    EodEligibleStage,
    'workstation_id' | 'assigned_to_user_id' | 'inspector_user_id' | 'verifier_user_id'
  >
): boolean {
  return (
    !(stage.workstation_id ?? '').trim() ||
    !(stage.assigned_to_user_id ?? '').trim() ||
    !(stage.inspector_user_id ?? '').trim() ||
    !(stage.verifier_user_id ?? '').trim()
  );
}

/** Completed stage (closed work orders included): the row is a log-only back entry. */
export function isBackEntryStage(stage: Pick<EodEligibleStage, 'status'>): boolean {
  return stage.status === 'Completed';
}

export function needsAssignmentForm(stage: EodEligibleStage): boolean {
  if (isBackEntryStage(stage)) return false;
  return stage.status === 'Pending' || hasMissingAssignments(stage);
}

export function defaultEodOutcomeForStatus(status: string): EodCloseOutcome {
  if (status === 'In Progress') return 'COMPLETE';
  if (status === 'Paused') return 'PAUSE';
  return 'COMPLETE';
}

export function eodBoqLineLabel(stage: EodEligibleStage): string {
  const parts: string[] = [];
  if (stage.line_number != null) parts.push(`L${stage.line_number}`);
  const code = (stage.item_code ?? '').trim();
  const desc = (stage.item_description ?? '').trim().replace(/\s+/g, ' ');
  if (code && desc) parts.push(`${code} — ${desc}`);
  else if (desc) parts.push(desc);
  else if (code) parts.push(code);
  return parts.length ? parts.join(' · ') : 'No BOQ line';
}

export function eodQtyLabel(stage: EodEligibleStage): string {
  const qty = stage.planned_quantity;
  if (qty == null) return 'Qty —';
  const u = (stage.uom ?? '').trim();
  const q = Number.isInteger(qty) ? String(qty) : String(qty);
  return u ? `Qty ${q} ${u}` : `Qty ${q}`;
}

export function canAccessEodClose(permissions: string[] | undefined, isSuperAdmin: boolean): boolean {
  if (isSuperAdmin) return true;
  const perms = permissions ?? [];
  return perms.some(
    (p) =>
      p === EOD_CLOSE_PERMISSION ||
      p === EOD_COMPLETE_PERMISSION ||
      p.startsWith(`${EOD_CLOSE_PERMISSION}`)
  );
}

/** Formats ISO datetimes in work-log lines to local `YYYY-MM-DD HH:mm`. */
export function formatOperationWorkLogLine(line: string): string {
  return line.replace(ISO_DT_RE, (raw) => {
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return raw;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    const h = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d} ${h}:${min}`;
  });
}

export function parseWorkLogLines(notes: string | null | undefined): string[] {
  return String(notes ?? '')
    .split(/\r?\n/)
    .map((e) => e.trim())
    .filter(Boolean)
    .map(formatOperationWorkLogLine);
}

// ---------------------------------------------------------------------------
// Register (one editable row per stage, any work order)
// ---------------------------------------------------------------------------

export const EOD_MAX_ROW_HOURS = 24;

/** Editable values for one register row. Times are local `HH:mm` on the sheet work date. */
export type EodRegisterRow = {
  stageId: string;
  outcome: EodCloseOutcome;
  startTime: string;
  endTime: string;
  hours: string;
  qty: string;
  workstationId: string | null;
  operatorId: string | null;
  inspectorId: string | null;
  verifierId: string | null;
  notes: string;
  /** Idempotency key sent with the row; kept until the row saves successfully. */
  clientRowKey: string;
  dirty: boolean;
};

export type EodRegisterPatch = Partial<Omit<EodRegisterRow, 'stageId' | 'clientRowKey' | 'dirty'>>;

export function newClientRowKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `eod-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function todayIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function createRegisterRow(stage: EodEligibleStage, clientRowKey = newClientRowKey()): EodRegisterRow {
  return {
    stageId: stage.id,
    outcome: defaultEodOutcomeForStatus(stage.status),
    startTime: '',
    endTime: '',
    hours: '',
    qty: '',
    workstationId: stage.workstation_id,
    operatorId: stage.assigned_to_user_id,
    inspectorId: stage.inspector_user_id,
    verifierId: stage.verifier_user_id,
    notes: '',
    clientRowKey,
    dirty: false,
  };
}

const HM_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function hmToMinutes(hm: string): number | null {
  const m = HM_RE.exec(hm.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Hours between two `HH:mm` times; an end before the start is treated as past midnight. */
export function hoursBetween(startHm: string, endHm: string): number | null {
  const s = hmToMinutes(startHm);
  const e = hmToMinutes(endHm);
  if (s == null || e == null) return null;
  const diff = e >= s ? e - s : e + 24 * 60 - s;
  return Math.round((diff / 60) * 100) / 100;
}

/** Local `HH:mm` for an ISO datetime (empty when missing / invalid). */
export function isoToLocalHm(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** ISO for a local `HH:mm` on `workDate`; `nextDay` shifts by one day (overnight end). */
export function localDateTimeIso(workDate: string, hm: string, nextDay = false): string | null {
  const mins = hmToMinutes(hm);
  if (mins == null || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) return null;
  const [y, mo, d] = workDate.split('-').map(Number);
  const dt = new Date(y, mo - 1, d + (nextDay ? 1 : 0), Math.floor(mins / 60), mins % 60, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
}

/** Apply edits to a row; start/end changes recompute hours when both times are set. */
export function applyRegisterPatch(row: EodRegisterRow, patch: EodRegisterPatch): EodRegisterRow {
  const next: EodRegisterRow = { ...row, ...patch, dirty: true };
  if (('startTime' in patch || 'endTime' in patch) && !('hours' in patch)) {
    const h = hoursBetween(next.startTime, next.endTime);
    if (h != null) next.hours = String(h);
  }
  return next;
}

/** "Copy yesterday": times, hours, operator, workstation and notes from the stage's last saved row. */
export function copyFromLastProgress(row: EodRegisterRow, stage: EodEligibleStage): EodRegisterRow {
  const last = stage.last_progress;
  if (!last) return row;
  const patch: EodRegisterPatch = {
    startTime: isoToLocalHm(last.started_at),
    endTime: isoToLocalHm(last.ended_at),
    hours: last.hours > 0 ? String(last.hours) : row.hours,
    notes: last.notes ?? row.notes,
  };
  if (last.operator_user_id) patch.operatorId = last.operator_user_id;
  if (last.workstation_id) patch.workstationId = last.workstation_id;
  return { ...row, ...patch, dirty: true };
}

export function rowNeedsAssignments(stage: EodEligibleStage): boolean {
  return needsAssignmentForm(stage);
}

export function rowRequiresQty(row: Pick<EodRegisterRow, 'outcome'>, stage: EodEligibleStage): boolean {
  return !isBackEntryStage(stage) && row.outcome === 'COMPLETE' && requiresCompletedQty(stage);
}

/** Per-row client validation (mirrors backend eodCloseOneStage gates). */
export function validateRegisterRow(row: EodRegisterRow, stage: EodEligibleStage): string | null {
  const hoursRaw = row.hours.trim();
  if (!hoursRaw) return 'Enter hours (0 or more)';
  const hours = Number(hoursRaw);
  if (!Number.isFinite(hours) || hours < 0) return 'Enter hours (0 or more)';
  if (hours > EOD_MAX_ROW_HOURS) return `Hours cannot exceed ${EOD_MAX_ROW_HOURS}`;
  if ((row.startTime && hmToMinutes(row.startTime) == null) || (row.endTime && hmToMinutes(row.endTime) == null)) {
    return 'Times must be HH:mm';
  }
  if (!row.notes.trim()) return 'Notes are required';
  const qtyRaw = row.qty.trim();
  if (qtyRaw) {
    const q = Number(qtyRaw);
    if (!Number.isFinite(q) || q < 0) return 'Qty must be 0 or more';
  } else if (rowRequiresQty(row, stage)) {
    return 'Enter qty done for Complete';
  }
  if (isBackEntryStage(stage)) {
    if (!(row.workstationId ?? '').trim() || !(row.operatorId ?? '').trim()) {
      return 'Select Workstation and Operator';
    }
    return null;
  }
  const assignmentsMissing =
    !(row.workstationId ?? '').trim() ||
    !(row.operatorId ?? '').trim() ||
    !(row.inspectorId ?? '').trim() ||
    !(row.verifierId ?? '').trim();
  if (assignmentsMissing) return 'Select Workstation, Operator, Inspector and Verifier';
  return null;
}

/** Work date on the sheet: not in the future. The server rejects rows dated before their work order date. */
export function validateWorkDate(workDate: string, today: string = todayIsoDate()): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate)) return 'Pick a work date';
  if (workDate > today) return 'Work date cannot be in the future';
  return null;
}

function changed(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim() !== (b ?? '').trim();
}

/** One POST item per row (each row carries its own hours / qty / times / notes / assignments). */
export function buildRegisterBatchItems(
  rows: EodRegisterRow[],
  stagesById: Map<string, EodEligibleStage>,
  workDate: string
): EodCloseStageInput[] {
  const items: EodCloseStageInput[] = [];
  for (const row of rows) {
    const stage = stagesById.get(row.stageId);
    if (!stage) continue;
    const qtyRaw = row.qty.trim();
    const qty = qtyRaw ? Number(qtyRaw) : null;
    const startMin = hmToMinutes(row.startTime);
    const endMin = hmToMinutes(row.endTime);
    const overnight = startMin != null && endMin != null && endMin < startMin;
    const startedAt = row.startTime ? localDateTimeIso(workDate, row.startTime) : null;
    const endedAt = row.endTime ? localDateTimeIso(workDate, row.endTime, overnight) : null;
    if (isBackEntryStage(stage)) {
      // Operator / workstation go on the log only; the completed stage is not reassigned.
      items.push({
        stageId: row.stageId,
        outcome: 'COMPLETE',
        actualHours: Number(row.hours.trim()),
        notes: row.notes.trim(),
        qtyDone: qty,
        completedQty: qty,
        workDate,
        clientRowKey: row.clientRowKey,
        autoPassInspection: false,
        startedAt,
        endedAt,
        workstationId: row.workstationId,
        assignedToUserId: row.operatorId,
      });
      continue;
    }
    const sendAssignments =
      needsAssignmentForm(stage) ||
      changed(row.workstationId, stage.workstation_id) ||
      changed(row.operatorId, stage.assigned_to_user_id) ||
      changed(row.inspectorId, stage.inspector_user_id) ||
      changed(row.verifierId, stage.verifier_user_id);
    items.push({
      stageId: row.stageId,
      outcome: row.outcome,
      actualHours: Number(row.hours.trim()),
      notes: row.notes.trim(),
      qtyDone: qty,
      completedQty: qty,
      workDate,
      clientRowKey: row.clientRowKey,
      autoPassInspection: row.outcome === 'COMPLETE',
      startedAt,
      endedAt,
      ...(sendAssignments
        ? {
            workstationId: row.workstationId,
            assignedToUserId: row.operatorId,
            inspectorUserId: row.inspectorId,
            verifierUserId: row.verifierId,
          }
        : {}),
    });
  }
  return items;
}

export function registerOutcomeCounts(rows: Pick<EodRegisterRow, 'outcome'>[]): Record<EodCloseOutcome, number> {
  const counts: Record<EodCloseOutcome, number> = { COMPLETE: 0, PAUSE: 0, IN_PROGRESS: 0 };
  for (const r of rows) counts[r.outcome] += 1;
  return counts;
}

// ---------------------------------------------------------------------------
// Draft (unsaved rows kept on the device per user / company / work date)
// ---------------------------------------------------------------------------

export function eodDraftStorageKey(userId: string, companyId: string, workDate: string): string {
  return `eod-register-draft:${userId || 'anon'}:${companyId || 'none'}:${workDate}`;
}

export function serializeEodDraft(rows: EodRegisterRow[]): string {
  return JSON.stringify(rows.filter((r) => r.dirty));
}

export function parseEodDraft(raw: string | null | undefined): EodRegisterRow[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is EodRegisterRow =>
        !!r && typeof r === 'object' && typeof r.stageId === 'string' && typeof r.clientRowKey === 'string'
    );
  } catch {
    return [];
  }
}

/** Fresh rows for the stage list, keeping draft values for stages still eligible. */
export function mergeDraftRows(
  stages: EodEligibleStage[],
  current: Record<string, EodRegisterRow>,
  draft: EodRegisterRow[] = []
): Record<string, EodRegisterRow> {
  const draftById = new Map(draft.map((r) => [r.stageId, r]));
  const out: Record<string, EodRegisterRow> = {};
  for (const s of stages) {
    const existing = current[s.id];
    if (existing?.dirty) out[s.id] = existing;
    else if (draftById.has(s.id)) out[s.id] = { ...draftById.get(s.id)!, dirty: true };
    else out[s.id] = createRegisterRow(s, existing?.clientRowKey);
  }
  return out;
}
